import { Component, EventEmitter, Input, OnChanges, OnInit, Output, SimpleChanges } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatSelectModule } from '@angular/material/select';
import { MatInputModule } from '@angular/material/input';
import { MatIconModule } from '@angular/material/icon';
import { MatButtonModule } from '@angular/material/button';
import { MatTooltipModule } from '@angular/material/tooltip';
import { NgxCurrencyDirective } from 'ngx-currency';
import { DescontoPerfilStruct } from '../../services/structs/vendas/desconto-perfil.struct';
import { PerfilOptionStruct } from '../../services/structs/usuario/perfil-option.struct';
import { MaskService } from '../../services/mask.service';

interface DescontoRow {
  idPerfil: number | null;
  descontoMaximo: number;
}

/**
 * Componente reutilizável de "Desconto máximo por perfil".
 * Segue o padrão visual das "Faixas de Comissão" do cadastro de vendedor: "Adicionar" insere uma linha
 * (perfil + % + remover), com estado vazio. Usado pela Configuração do PDV e reutilizável pelo Orçamento.
 */
@Component({
  selector: 'app-desconto-maximo-perfil',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    MatFormFieldModule,
    MatSelectModule,
    MatInputModule,
    MatIconModule,
    MatButtonModule,
    MatTooltipModule,
    NgxCurrencyDirective,
  ],
  templateUrl: './desconto-maximo-perfil.component.html',
  styleUrls: ['./desconto-maximo-perfil.component.css'],
})
export class DescontoMaximoPerfilComponent implements OnChanges, OnInit {
  @Input() perfis: PerfilOptionStruct[] = [];
  @Input() value: DescontoPerfilStruct[] = [];
  @Input() disabled: boolean = false;
  @Input() titulo: string = 'Desconto máximo por perfil';
  /** Quando preenchido, exibe o ícone `info` com este texto em tooltip ao lado do título. */
  @Input() tooltip: string = '';
  /** Título do estado vazio. O default preserva a frase única usada pelo PDV e pelo Orçamento. */
  @Input() textoVazio: string = 'Nenhum perfil configurado — o desconto base se aplica a todos os usuários.';
  /** Texto de apoio do estado vazio. Só é exibido no padrão de subseção. */
  @Input() textoVazioApoio: string = '';

  @Input() padraoSecao: boolean = false;
  @Output() valueChange = new EventEmitter<DescontoPerfilStruct[]>();

  public items: DescontoRow[] = [];
  public percentMask: any;
  private internalUpdate = false;

  constructor(private maskService: MaskService) {}

  ngOnInit(): void {
    this.percentMask = this.maskService.getPercentMask();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['value']) {
      // Ignora o eco da própria emissão para não descartar linhas em edição (ex.: perfil ainda não escolhido).
      if (this.internalUpdate) {
        this.internalUpdate = false;
        return;
      }
      this.items = (this.value || []).map(x => ({ idPerfil: x.idPerfil, descontoMaximo: x.descontoMaximo }));
    }
  }

  public get podeAdicionar(): boolean {
    return !this.disabled && this.items.length < this.perfis.length;
  }

  public get tooltipAdicionar(): string {
    if (this.perfis.length === 0) return 'Nenhum perfil cadastrado no sistema.';
    if (this.items.length >= this.perfis.length) return 'Todos os perfis já foram adicionados';
    return 'Adicionar perfil';
  }

  /** Tooltip exibido quando o campo Perfil de uma linha não tem nenhuma opção para mostrar. */
  public tooltipPerfilVazio(rowIndex: number): string {
    if (this.perfis.length === 0) return 'Nenhum perfil cadastrado no sistema.';
    return 'Todos os perfis já foram selecionados em outras linhas.';
  }

  public perfisDisponiveis(rowIndex: number): PerfilOptionStruct[] {
    return this.perfis.filter(
      p => !this.items.some((it, j) => j !== rowIndex && it.idPerfil === p.idPerfil)
    );
  }

  public adicionar(): void {
    if (!this.podeAdicionar) return;
    this.items = [...this.items, { idPerfil: null, descontoMaximo: 0 }];
  }

  public onPerfilChange(index: number, idPerfil: number): void {
    this.items[index].idPerfil = idPerfil;
    this.emit();
  }

  public onDescontoChange(index: number, valor: number): void {
    this.items[index].descontoMaximo = this.clamp(valor);
    this.emit();
  }

  public remover(index: number): void {
    this.items.splice(index, 1);
    this.emit();
  }

  private clamp(valor: number): number {
    const n = Number(valor);
    if (isNaN(n)) return 0;
    return Math.min(100, Math.max(0, n));
  }

  private emit(): void {
    this.internalUpdate = true;
    this.valueChange.emit(
      this.items
        .filter(x => x.idPerfil != null)
        .map(x => ({ idPerfil: x.idPerfil as number, descontoMaximo: x.descontoMaximo }))
    );
  }
}
